'use client';

import { memo, useCallback, useEffect, useRef, useState } from 'react';
import { NodeToolbar, Position } from '@xyflow/react';
import {
  AlignCenter,
  AlignLeft,
  AlignRight,
  AlignVerticalJustifyCenter,
  AlignVerticalJustifyEnd,
  AlignVerticalJustifyStart,
  Ban,
  Bold,
  ChevronDown,
  Files,
  MoreHorizontal,
  Trash2,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { haptic } from '@/lib/haptic';
import {
  useWorkspaceActions,
  useWorkspaceItem,
} from '@/components/workspace/store';
import { useWorkspaceSelection } from '@/components/workspace/selection-context';
import { useT } from '@/components/i18n-provider';
import type {
  AnnotationColor,
  AnnotationKind,
  AnnotationShape,
  AnnotationStroke,
} from '@/lib/workspaces/types';

/**
 * Figjam / Confluence-style floating toolbar that sits above the
 * selected annotation node. Mounted by `AnnotationNode` when the node
 * is selected AND not in text-edit mode.
 *
 * Single source of truth for annotation property edits — the right-
 * click context menu for annotations is intentionally suppressed so
 * both surfaces can't render simultaneously.
 *
 * Control layout (matches the user's "logical grouping" feedback):
 *
 *   [ Kind ▼ ] | [ Fill ▼ ] [ Shape ▼ ] [ Border ▼ ]
 *              | [ Size ▼ ] [ Align ▼ ] [ B ]
 *              | [ 🗑 ]
 *
 * - "Kind" sits first because it tells the user what the annotation
 *   IS (Note / Text / Shape) and gates which downstream controls are
 *   even meaningful.
 * - "Fill" is the next visually dominant property (background colour
 *   for stickies / shapes); Shape and Border are shape-specific and
 *   cluster next to it.
 * - The text cluster (Size, Align, Bold) is always present —
 *   stickies, text nodes, and shapes all carry user-typed copy.
 * - "Delete" sits in its own group at the far right so it can't be
 *   confused with a property dropdown.
 *
 * `NodeToolbar` from `@xyflow/react` positions the bar above the
 * node and tracks the viewport transform automatically. We use
 * `nodrag nopan` so canvas gestures don't steal toolbar clicks, and
 * `pointer-events: none` on the wrapper (with `auto` on buttons)
 * so clicks in the toolbar's empty space pass THROUGH to whatever
 * annotation / pane is underneath — fixes the "selecting another
 * annotation through the toolbar doesn't work" symptom.
 */

// ----------------------------------------------------------------------
// Palettes / static data
// ----------------------------------------------------------------------
const PALETTE: AnnotationColor[] = [
  'yellow',
  'green',
  'pink',
  'blue',
  'violet',
  'amber',
  'gray',
];

const PALETTE_HEX: Record<AnnotationColor, { light: string; dark: string }> = {
  yellow: { light: '#FEF3C7', dark: '#5A4A0E' },
  green: { light: '#DCFCE7', dark: '#10371F' },
  pink: { light: '#FCE7F3', dark: '#4A1031' },
  blue: { light: '#DBEAFE', dark: '#1B3553' },
  violet: { light: '#EDE9FE', dark: '#2D1B5C' },
  amber: { light: '#FFEDD5', dark: '#5B2912' },
  gray: { light: '#F1F5F9', dark: '#283242' },
};

const KIND_LABEL: Record<AnnotationKind, string> = {
  sticky: 'Note',
  text: 'Text',
  shape: 'Shape',
  // 'link' annotations are spawned by URL paste — they're not part
  // of the sticky↔text↔shape "Convert to" cycle (converting would
  // lose the OG metadata). The label is here only to satisfy
  // `Record<AnnotationKind, …>` exhaustiveness; the kind-picker UI
  // filters by `KIND_ORDER` which deliberately excludes `'link'`.
  link: 'Link',
};

const KIND_ORDER: AnnotationKind[] = ['sticky', 'text', 'shape'];

const SHAPES: AnnotationShape[] = ['rectangle', 'ellipse', 'diamond', 'triangle'];

const STROKES: { id: AnnotationStroke; label: string }[] = [
  { id: 'solid', label: 'Solid' },
  { id: 'dashed', label: 'Dashed' },
  { id: 'dotted', label: 'Dotted' },
  // `'none'` lives in its own group below the dashed variants in
  // the popover — see render code — but ships in the same array so
  // the active-state lookup is uniform.
  { id: 'none', label: 'No border' },
];

const FONT_SIZES: { id: 'sm' | 'md' | 'lg' | 'xl'; label: string }[] = [
  { id: 'sm', label: 'Small' },
  { id: 'md', label: 'Medium' },
  { id: 'lg', label: 'Large' },
  { id: 'xl', label: 'Extra large' },
];

type TextAlign = 'left' | 'center' | 'right';

const ALIGN_OPTIONS: {
  id: TextAlign;
  label: string;
  icon: typeof AlignLeft;
}[] = [
  { id: 'left', label: 'Left', icon: AlignLeft },
  { id: 'center', label: 'Center', icon: AlignCenter },
  { id: 'right', label: 'Right', icon: AlignRight },
];

type VerticalAlign = 'top' | 'middle' | 'bottom';

const V_ALIGN_OPTIONS: ReadonlyArray<{
  id: VerticalAlign;
  label: string;
  icon: typeof AlignVerticalJustifyStart;
}> = [
  { id: 'top', label: 'Top', icon: AlignVerticalJustifyStart },
  { id: 'middle', label: 'Middle', icon: AlignVerticalJustifyCenter },
  { id: 'bottom', label: 'Bottom', icon: AlignVerticalJustifyEnd },
];

// ----------------------------------------------------------------------
// Component
// ----------------------------------------------------------------------
type OpenPopover =
  | 'kind'
  | 'fill'
  | 'shape'
  | 'border'
  | 'size'
  | 'align'
  | null;

function AnnotationToolbarImpl({
  itemId,
  kind,
  color,
  shape,
}: {
  itemId: string;
  kind: AnnotationKind;
  color: AnnotationColor;
  shape: AnnotationShape;
}) {
  const t = useT();
  const item = useWorkspaceItem(itemId);
  const { patchItemState, removeItem } = useWorkspaceActions();
  const [open, setOpen] = useState<OpenPopover>(null);
  const toolbarRef = useRef<HTMLDivElement | null>(null);
  // Suppress this per-item floating toolbar when the user is editing
  // a multi-select. The unified `SelectionToolbar` takes over (Delete
  // + Fit) and stacking N annotation toolbars would clutter the
  // canvas. Single-item selection (count <= 1) keeps the rich
  // per-kind property controls.
  const selectionCount = useWorkspaceSelection().count;

  const cache = item?.state.cache ?? {};
  const fontWeight =
    (cache.fontWeight as 'regular' | 'bold' | undefined) ?? 'regular';
  const fontSize =
    (cache.fontSize as 'sm' | 'md' | 'lg' | 'xl' | undefined) ?? 'md';
  const stroke =
    (cache.strokeStyle as AnnotationStroke | undefined) ?? 'solid';
  // Defaults need to mirror what `annotation-node.tsx` renders for
  // the same `kind` — otherwise the toolbar would show "Left" as
  // active for a freshly-spawned shape whose label visibly sits
  // centred. Keeping the two sides in lock-step makes the popovers
  // an honest mirror of the current rendered state.
  const textAlign =
    (cache.textAlign as TextAlign | undefined) ??
    (kind === 'shape' ? 'center' : 'left');
  const verticalAlign =
    (cache.verticalAlign as VerticalAlign | undefined) ??
    (kind === 'shape' ? 'middle' : 'top');
  const fillEnabled = cache.fillEnabled !== false;

  const setCache = useCallback(
    (patch: Record<string, unknown>) => {
      patchItemState(itemId, { cache: patch }, { debounceMs: 200 });
      haptic('light');
    },
    [itemId, patchItemState],
  );

  const togglePopover = useCallback((id: NonNullable<OpenPopover>) => {
    setOpen((cur) => (cur === id ? null : id));
  }, []);

  useEffect(() => {
    if (!open) return;
    const onPointer = (e: PointerEvent) => {
      const node = toolbarRef.current;
      if (!node) return;
      if (e.target instanceof Node && node.contains(e.target)) return;
      setOpen(null);
    };
    document.addEventListener('pointerdown', onPointer, true);
    return () => document.removeEventListener('pointerdown', onPointer, true);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(null);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open]);

  // ---- Per-kind visibility flags ---------------------------------------
  // Fill applies to stickies and shapes — text annotations have no
  // background.
  const showFill = kind === 'sticky' || kind === 'shape';
  const showShape = kind === 'shape';
  const showBorder = kind === 'shape';
  // Text controls apply universally — even shapes carry a label.
  const showText = true;

  const ActiveAlignIcon = ALIGN_OPTIONS.find((a) => a.id === textAlign)?.icon
    ?? AlignLeft;

  if (selectionCount > 1) return null;

  return (
    <NodeToolbar position={Position.Top} offset={16} isVisible>
      <div
        ref={toolbarRef}
        className={cn(
          'nodrag nopan select-none',
          'flex items-center gap-0.5 rounded-xl',
          'bg-background/95 backdrop-blur-md',
          'border border-border/70 shadow-lg',
          'px-1 py-1 text-foreground',
          // `pointer-events: none` on the container with `auto` on
          // every descendant button — clicks in the toolbar's empty
          // space (gaps between buttons, area that overlaps a
          // neighbouring annotation, etc.) fall through to the
          // canvas so selection toggling stays responsive.
          '[&_button]:pointer-events-auto pointer-events-none',
        )}
        onPointerDown={(e) => e.stopPropagation()}
        onMouseDown={(e) => e.stopPropagation()}
        onClick={(e) => e.stopPropagation()}
      >
        {/* ============================================================ */}
        {/* Group 1: KIND — what is this annotation?                     */}
        {/* ============================================================ */}
        <div className="relative">
          <ToolButton
            ariaLabel="Annotation kind"
            onClick={() => togglePopover('kind')}
            active={open === 'kind'}
          >
            <span className="text-xs font-medium">{KIND_LABEL[kind]}</span>
            <Caret />
          </ToolButton>
          {open === 'kind' && (
            <Popover label={t('workspace.toolbar.convertTo')}>
              <div className="flex flex-col gap-0.5 min-w-[140px]">
                {KIND_ORDER.map((k) => (
                  <MenuItem
                    key={k}
                    label={KIND_LABEL[k]}
                    active={k === kind}
                    onClick={() => {
                      setCache({ kind: k });
                      setOpen(null);
                    }}
                  />
                ))}
              </div>
            </Popover>
          )}
        </div>

        {/* ============================================================ */}
        {/* Group 2: SHAPE PROPS — fill, shape, border                   */}
        {/* ============================================================ */}
        {(showFill || showShape || showBorder) && <Divider />}

        {showFill && (
          <div className="relative">
            <ToolButton
              ariaLabel="Fill"
              onClick={() => togglePopover('fill')}
              active={open === 'fill'}
              title={t('workspace.toolbar.fillColor')}
            >
              {fillEnabled ? (
                <span
                  className="block h-4 w-4 rounded-[3px] border border-foreground/15 shadow-inner"
                  style={{ background: PALETTE_HEX[color].light }}
                  aria-hidden
                />
              ) : (
                <NoFillSwatch />
              )}
              <Caret />
            </ToolButton>
            {open === 'fill' && (
              <Popover label={t('workspace.toolbar.fill')}>
                <div className="grid grid-cols-7 gap-1.5">
                  {PALETTE.map((c) => (
                    <button
                      key={c}
                      type="button"
                      onClick={() => {
                        // Picking a colour implicitly enables fill —
                        // the user expects clicking a swatch to make
                        // the swatch's colour visible even if "No
                        // fill" was just selected.
                        setCache({ color: c, fillEnabled: true });
                        setOpen(null);
                      }}
                      aria-label={`Fill ${c}`}
                      className={cn(
                        'h-7 w-7 rounded-md border transition-transform',
                        'hover:scale-110',
                        fillEnabled && c === color
                          ? 'border-foreground/80 ring-2 ring-primary/40'
                          : 'border-foreground/15',
                      )}
                      style={{ background: PALETTE_HEX[c].light }}
                    />
                  ))}
                </div>
                <div className="mt-2 pt-2 border-t border-border/40">
                  <button
                    type="button"
                    onClick={() => {
                      setCache({ fillEnabled: false });
                      setOpen(null);
                    }}
                    className={cn(
                      'w-full inline-flex items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm',
                      'hover:bg-foreground/8',
                      !fillEnabled && 'bg-foreground/8 font-medium',
                    )}
                  >
                    <NoFillSwatch />
                    <span>{t('workspace.toolbar.noFill')}</span>
                    {!fillEnabled && (
                      <span className="ml-auto text-xs text-muted-foreground">
                        ✓
                      </span>
                    )}
                  </button>
                </div>
              </Popover>
            )}
          </div>
        )}

        {showShape && (
          <div className="relative">
            <ToolButton
              ariaLabel="Shape"
              onClick={() => togglePopover('shape')}
              active={open === 'shape'}
              title={`Shape: ${shape}`}
            >
              <ShapeIcon shape={shape} className="h-4 w-4" />
              <Caret />
            </ToolButton>
            {open === 'shape' && (
              <Popover label={t('workspace.zoom.shape')}>
                <div className="flex items-center gap-1">
                  {SHAPES.map((s) => (
                    <button
                      key={s}
                      type="button"
                      onClick={() => {
                        setCache({ shape: s });
                        setOpen(null);
                      }}
                      aria-label={`Shape ${s}`}
                      title={s}
                      className={cn(
                        'inline-flex h-8 w-8 items-center justify-center rounded-md border transition-colors',
                        s === shape
                          ? 'border-primary bg-primary/10 text-primary'
                          : 'border-transparent hover:bg-foreground/8',
                      )}
                    >
                      <ShapeIcon shape={s} className="h-4 w-4" />
                    </button>
                  ))}
                </div>
              </Popover>
            )}
          </div>
        )}

        {showBorder && (
          <div className="relative">
            <ToolButton
              ariaLabel="Border"
              onClick={() => togglePopover('border')}
              active={open === 'border'}
              title={t('workspace.toolbar.borderStyle')}
            >
              <StrokePreview stroke={stroke} />
              <Caret />
            </ToolButton>
            {open === 'border' && (
              <Popover label={t('workspace.toolbar.border')}>
                <div className="flex flex-col gap-0.5 min-w-[150px]">
                  {STROKES.map((s) => (
                    <MenuItem
                      key={s.id}
                      active={s.id === stroke}
                      onClick={() => {
                        setCache({ strokeStyle: s.id });
                        setOpen(null);
                      }}
                      label={
                        <span className="inline-flex items-center gap-2">
                          <StrokePreview stroke={s.id} />
                          {s.label}
                        </span>
                      }
                    />
                  ))}
                </div>
              </Popover>
            )}
          </div>
        )}

        {/* ============================================================ */}
        {/* Group 3: TEXT PROPS — size, align, weight                    */}
        {/* ============================================================ */}
        {showText && <Divider />}

        {showText && (
          <div className="relative">
            <ToolButton
              ariaLabel="Text size"
              onClick={() => togglePopover('size')}
              active={open === 'size'}
              title={t('workspace.toolbar.textSize')}
            >
              <span className="inline-flex items-baseline gap-0.5">
                <span className="text-xs font-medium">T</span>
                <span className="text-[10px] font-semibold tabular-nums opacity-75">
                  {fontSize.toUpperCase()}
                </span>
              </span>
              <Caret />
            </ToolButton>
            {open === 'size' && (
              <Popover label={t('workspace.toolbar.textSize')}>
                <div className="flex flex-col gap-0.5 min-w-[140px]">
                  {FONT_SIZES.map((s) => (
                    <MenuItem
                      key={s.id}
                      active={s.id === fontSize}
                      onClick={() => {
                        setCache({ fontSize: s.id });
                        setOpen(null);
                      }}
                      label={
                        <span className="inline-flex items-center gap-2">
                          <span
                            className={cn(
                              'inline-flex h-6 w-6 items-center justify-center rounded border border-foreground/15 font-semibold',
                              s.id === 'sm' && 'text-[10px]',
                              s.id === 'md' && 'text-[12px]',
                              s.id === 'lg' && 'text-[14px]',
                              s.id === 'xl' && 'text-[16px]',
                            )}
                          >
                            T
                          </span>
                          {s.label}
                        </span>
                      }
                    />
                  ))}
                </div>
              </Popover>
            )}
          </div>
        )}

        {showText && (
          <div className="relative">
            <ToolButton
              ariaLabel="Text alignment"
              onClick={() => togglePopover('align')}
              active={open === 'align'}
              title={t('workspace.toolbar.textAlignment')}
            >
              <ActiveAlignIcon className="h-3.5 w-3.5" />
              <Caret />
            </ToolButton>
            {open === 'align' && (
              <Popover label={t('workspace.toolbar.alignment')}>
                <div className="flex items-center gap-1">
                  {ALIGN_OPTIONS.map((a) => {
                    const Icon = a.icon;
                    return (
                      <button
                        key={a.id}
                        type="button"
                        onClick={() => {
                          setCache({ textAlign: a.id });
                          setOpen(null);
                        }}
                        aria-label={a.label}
                        title={a.label}
                        className={cn(
                          'inline-flex h-8 w-8 items-center justify-center rounded-md border transition-colors',
                          a.id === textAlign
                            ? 'border-primary bg-primary/10 text-primary'
                            : 'border-transparent hover:bg-foreground/8',
                        )}
                      >
                        <Icon className="h-4 w-4" />
                      </button>
                    );
                  })}
                </div>
                <div className="mt-2 pt-2 border-t border-border/40">
                  <div className="px-1 pb-1 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                    Vertical
                  </div>
                  <div className="flex items-center gap-1">
                    {V_ALIGN_OPTIONS.map((a) => {
                      const Icon = a.icon;
                      return (
                        <button
                          key={a.id}
                          type="button"
                          onClick={() => {
                            setCache({ verticalAlign: a.id });
                            setOpen(null);
                          }}
                          aria-label={a.label}
                          title={a.label}
                          className={cn(
                            'inline-flex h-8 w-8 items-center justify-center rounded-md border transition-colors',
                            a.id === verticalAlign
                              ? 'border-primary bg-primary/10 text-primary'
                              : 'border-transparent hover:bg-foreground/8',
                          )}
                        >
                          <Icon className="h-4 w-4" />
                        </button>
                      );
                    })}
                  </div>
                </div>
              </Popover>
            )}
          </div>
        )}

        {showText && (
          <ToolButton
            ariaLabel="Bold"
            onClick={() =>
              setCache({
                fontWeight: fontWeight === 'bold' ? 'regular' : 'bold',
              })
            }
            active={fontWeight === 'bold'}
            title={t('workspace.toolbar.bold')}
          >
            <Bold className="h-3.5 w-3.5" />
          </ToolButton>
        )}

        {/* ============================================================ */}
        {/* Group 4: UNIVERSAL EDIT — Duplicate + More + Delete          */}
        {/* ============================================================ */}
        {/*
          The duplicate / more / delete trio is the same triplet the
          unified `SelectionFloatingToolbar` shows for non-annotation
          selections — embedding it here means a single sticky has
          ONE toolbar (the per-kind one) that covers BOTH property
          editing AND cross-cutting actions (z-order, align, copy,
          cut, paste, focus, …). The "..." opens the canvas context
          menu at the toolbar's anchor via a global event the canvas
          listens for, so we don't have to thread the menu state
          through annotation-node → annotation-toolbar props.
        */}
        <Divider />
        <ToolButton
          ariaLabel="Duplicate"
          onClick={() => {
            window.dispatchEvent(
              new CustomEvent('workspace:duplicate-selection'),
            );
          }}
          title="Duplicate (⌘D)"
        >
          <Files className="h-3.5 w-3.5" />
        </ToolButton>
        <MoreActionsButton toolbarRef={toolbarRef} />
        <Divider />
        <ToolButton
          ariaLabel="Delete"
          onClick={() => {
            void removeItem(itemId);
          }}
          danger
          title={t('messages.delete')}
        >
          <Trash2 className="h-3.5 w-3.5" />
        </ToolButton>
      </div>
    </NodeToolbar>
  );
}

/**
 * "..." button — opens the unified canvas context menu at the
 * toolbar's bottom-right corner. Sourced from the registry by
 * `workspace-canvas.tsx`'s `workspace:open-more-menu` listener so
 * every command available in the right-click menu is reachable
 * from the per-kind toolbar without re-implementing the catalogue.
 */
function MoreActionsButton({
  toolbarRef,
}: {
  toolbarRef: React.RefObject<HTMLDivElement | null>;
}) {
  const t = useT();
  return (
    <ToolButton
      ariaLabel={t('workspace.toolbar.moreActions')}
      onClick={() => {
        const wrap = toolbarRef.current;
        if (!wrap) return;
        const rect = wrap.getBoundingClientRect();
        window.dispatchEvent(
          new CustomEvent('workspace:open-more-menu', {
            // Anchor at the toolbar's bottom-left so the menu drops
            // downward and doesn't cover the toolbar itself.
            detail: { x: rect.left, y: rect.bottom + 4 },
          }),
        );
      }}
      title={t('workspace.toolbar.moreActions')}
    >
      <MoreHorizontal className="h-3.5 w-3.5" />
    </ToolButton>
  );
}

// ----------------------------------------------------------------------
// Sub-primitives
// ----------------------------------------------------------------------

function ToolButton({
  children,
  onClick,
  ariaLabel,
  title,
  active,
  danger,
}: {
  children: React.ReactNode;
  onClick: () => void;
  ariaLabel: string;
  title?: string;
  active?: boolean;
  danger?: boolean;
}) {
  return (
    <button
      type="button"
      aria-label={ariaLabel}
      aria-pressed={active}
      title={title ?? ariaLabel}
      onClick={(e) => {
        e.stopPropagation();
        onClick();
      }}
      className={cn(
        'inline-flex h-7 items-center justify-center gap-1 rounded-md px-2 transition-colors',
        'text-foreground/85 hover:bg-foreground/8 active:bg-foreground/12',
        active && 'bg-primary/12 text-primary',
        danger && 'hover:bg-red-500/10 hover:text-red-600 dark:hover:text-red-400',
      )}
    >
      {children}
    </button>
  );
}

function Caret() {
  return <ChevronDown className="h-3 w-3 opacity-60" />;
}

function Divider() {
  return <span aria-hidden className="mx-0.5 h-5 w-px bg-border/80" />;
}

function MenuItem({
  label,
  active,
  onClick,
}: {
  label: React.ReactNode;
  active?: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={(e) => {
        e.stopPropagation();
        onClick();
      }}
      className={cn(
        'flex items-center justify-between rounded-md px-2 py-1.5 text-left text-sm',
        'hover:bg-foreground/8',
        active && 'bg-foreground/8 font-medium',
      )}
    >
      <span className="inline-flex items-center">{label}</span>
      {active && <span className="text-xs text-muted-foreground">✓</span>}
    </button>
  );
}

function Popover({
  label,
  children,
}: {
  label?: string;
  children: React.ReactNode;
}) {
  return (
    <div
      className={cn(
        'absolute top-[calc(100%+8px)] left-0',
        'glass-panel rounded-lg p-2 shadow-2xl',
        'min-w-max',
        'pointer-events-auto',
        'z-[210]',
      )}
      role="menu"
      onPointerDown={(e) => e.stopPropagation()}
      onMouseDown={(e) => e.stopPropagation()}
      onClick={(e) => e.stopPropagation()}
    >
      {label && (
        <div className="px-1 pb-1.5 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
          {label}
        </div>
      )}
      {children}
    </div>
  );
}

function StrokePreview({ stroke }: { stroke: AnnotationStroke }) {
  // `'none'` is a Ø glyph so the active state in the trigger button
  // reads as "no border" rather than as an invisible / broken line.
  if (stroke === 'none') {
    return <Ban className="h-3.5 w-3.5 text-foreground/55" aria-hidden />;
  }
  return (
    <svg viewBox="0 0 28 6" className="h-1.5 w-7 text-foreground/85" aria-hidden>
      <line
        x1="1"
        y1="3"
        x2="27"
        y2="3"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeDasharray={
          stroke === 'dashed' ? '6 4' : stroke === 'dotted' ? '1 4' : undefined
        }
      />
    </svg>
  );
}

/**
 * Checkered "no fill" swatch — convention from Figma / Sketch. Two
 * diagonal half-triangles that read as "empty" without needing a
 * caption.
 */
function NoFillSwatch() {
  return (
    <svg
      viewBox="0 0 16 16"
      className="h-4 w-4 rounded-[3px] border border-foreground/15"
      aria-hidden
    >
      <rect x="0" y="0" width="16" height="16" fill="white" />
      <line
        x1="0"
        y1="16"
        x2="16"
        y2="0"
        stroke="#EF4444"
        strokeWidth="2"
      />
    </svg>
  );
}

function ShapeIcon({
  shape,
  className,
}: {
  shape: AnnotationShape;
  className?: string;
}) {
  const stroke = 'currentColor';
  const common = { fill: 'none', stroke, strokeWidth: 1.6 } as const;
  switch (shape) {
    case 'ellipse':
      return (
        <svg viewBox="0 0 16 16" className={className}>
          <ellipse cx="8" cy="8" rx="6.5" ry="6.5" {...common} />
        </svg>
      );
    case 'diamond':
      return (
        <svg viewBox="0 0 16 16" className={className}>
          <polygon points="8,1 15,8 8,15 1,8" {...common} />
        </svg>
      );
    case 'triangle':
      return (
        <svg viewBox="0 0 16 16" className={className}>
          <polygon points="8,2 15,14 1,14" {...common} />
        </svg>
      );
    case 'rectangle':
    default:
      return (
        <svg viewBox="0 0 16 16" className={className}>
          <rect x="1.5" y="1.5" width="13" height="13" rx="2" {...common} />
        </svg>
      );
  }
}

export const AnnotationToolbar = memo(AnnotationToolbarImpl);
